import { useState } from "react";
import { AlertOctagon } from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";
import { Input } from "@/components/atoms/studio/ui/input";
import { useEngineClearApply } from "@/hooks/migrations/engine";
import {
  CLEAR_APPLY_MIN_REASON,
  clearApplyReasonValid,
  derivedClearApplyToken,
} from "@/lib/migrations/apply";
import { engineErrorMessage, engineTaskPath } from "@/services/migrations/engine";
import type { EngineApplyAttempt, EngineTaskReport } from "@/services/migrations/engine";

/**
 * Close an apply attempt the engine could not settle. THE OPERATOR ESCAPE HATCH.
 *
 * WHAT IT IS. `apply.py` parks a task at `applying` when it cannot establish whether a
 * deploy reached the org, and that park has no timeout by design — but a safety state
 * whose only exit is a psql prompt is a safety state people route around. This is the
 * door. It reaches no org, it sends nothing, and it decides nothing.
 *
 * WHAT IT IS NOT, AND WHY THE COPY LABOURS THE POINT. It does NOT assert that the org is
 * untouched. The whole reason an attempt reaches this state is that nobody can know. It
 * records that a named person went and looked, and the attempt closes as
 * `APPLY_ABANDONED` — its own outcome, never one of the live ones — with the task landing
 * at `blocked` rather than `done`.
 *
 * WHY IT IS A MODAL WITH TWO REQUIRED FIELDS. The backend requires a verbatim token
 * naming the exact attempt and a reason of at least ten characters that goes into the
 * ledger word for word. Those are not form validation; they are the act. A dialog that
 * pre-filled either of them would be doing the operator's part of it for them.
 *
 * Built on `DestructiveRunDialog`'s pattern — the studio-native destructive confirm, with
 * the same overlay, roles and token set — rather than the `@ncoder/ui` Dialog, which
 * carries a different (non-studio) token set.
 */
export function EngineClearApplyDialog({
  planId,
  task,
  attempt,
  orgId,
  onClose,
}: {
  planId: string;
  task: EngineTaskReport;
  attempt: EngineApplyAttempt;
  orgId?: string | null;
  onClose: () => void;
}) {
  const [confirmation, setConfirmation] = useState("");
  const [reason, setReason] = useState("");
  const mutation = useEngineClearApply(planId, engineTaskPath(task));

  // DERIVED, and labelled as such. `/report` issues `apply.confirmation_token` for the
  // write path but nothing for this one, and the attempt row carries no compile key of
  // its own. A parked task cannot recompile while it is parked, so the task's key is the
  // attempt's — and when that assumption is ever wrong the server refuses with
  // CONFIRMATION_MISMATCH and returns the token it wanted, which is what then shows.
  const derived = derivedClearApplyToken(
    orgId ?? "",
    attempt.component_key || task.apply.will_create[0]?.component_key || "",
    task.compile_key
  );
  const expected = mutation.data?.expected || derived;

  const reasonOk = clearApplyReasonValid(reason);
  const tokenOk = Boolean(expected) && confirmation === expected;
  const settled = mutation.data && mutation.data.outcome === "APPLY_ABANDONED";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clear-apply-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-lg border border-destructive/40 bg-card shadow-xl animate-in zoom-in-95"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
            <AlertOctagon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div id="clear-apply-title" className="text-sm font-semibold text-foreground">
              Close a parked apply attempt
            </div>
            <div className="text-[11px] leading-relaxed text-muted-foreground">
              This does NOT undo a deploy and does NOT say the org is untouched. It records that
              you went and looked at{" "}
              <span className="font-mono text-foreground">
                {attempt.component_key || "this component"}
              </span>{" "}
              and are taking responsibility for the answer. The task will land at{" "}
              <span className="font-mono">blocked</span>, never <span className="font-mono">done</span>.
            </div>
          </div>
        </div>

        <div className="space-y-4 px-4 py-4">
          {settled ? (
            <div className="space-y-1 text-xs text-foreground">
              <p className="font-semibold">The attempt is closed.</p>
              <p className="max-w-prose leading-relaxed text-muted-foreground">
                {mutation.data?.message}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                  1 · what the org actually contained when you looked
                </label>
                <Input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={`At least ${CLEAR_APPLY_MIN_REASON} characters — this goes in the ledger verbatim`}
                  disabled={mutation.isPending}
                  className="font-mono text-xs"
                />
                <p className="text-[11px] text-muted-foreground">
                  Write the evidence, not the conclusion. Someone reading this a year from now
                  needs to know what you saw.
                </p>
              </div>

              <div className="space-y-1">
                <label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                  2 · type this sentence exactly
                </label>
                <p className="break-all rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-foreground">
                  {expected || "The token for this attempt could not be derived."}
                </p>
                {mutation.data?.expected ? (
                  <p className="text-[11px] text-[hsl(var(--accent-warm))]">
                    The server refused the previous token and returned this one. It is the
                    authoritative version.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Derived from the org, the component and this artifact&apos;s compile key. If it
                    is wrong the server will refuse and return the one it wants.
                  </p>
                )}
                <Input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder="ABANDON APPLY OF …"
                  disabled={mutation.isPending || !expected}
                  className="font-mono text-xs"
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>

              {mutation.data && !settled ? (
                <p className="space-y-1 rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                  <span className="font-mono uppercase">{mutation.data.code}</span> —{" "}
                  {mutation.data.message}
                </p>
              ) : null}
              {mutation.isError ? (
                <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                  {engineErrorMessage(mutation.error)}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-4 py-3">
          <Button size="sm" variant="ghost" onClick={onClose}>
            {settled ? "Close" : "Cancel"}
          </Button>
          {settled ? null : (
            <Button
              size="sm"
              variant="destructive"
              disabled={!reasonOk || !tokenOk || mutation.isPending}
              onClick={() => mutation.mutate({ confirmation, reason: reason.trim() })}
            >
              {mutation.isPending ? "Recording…" : "Record that I looked"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
